import { createSlice } from "@reduxjs/toolkit";
import { addQuery } from "helpers/utis";
import { api } from "service/api";
import { MasterQuantityAddAPIs } from "service/apiVariables";
import { Toast } from "service/toast";
import { setLoader } from "./loader";

const initialState = {
  projectSummary: {},
  workAreaOptions: [],
  tradeOptions: [],
  wings: [],
  activitiesByTrade: {},
  subActivitiesByActivity: {},
  isLoading: false,
  error: null,
  submitResponse: null,
  submitMasterQuantityApi: {
    api: "/project/master-quantity-add",
    baseURL: "user",
    method: "post",
  },
  updateMasterQuantityApi: {
    api: "/project/master-quantity-update",
    baseURL: "user",
    method: "put",
  },
};

export const MasterQuantityWorkSlice = createSlice({
  name: "Master Quantity Work",
  initialState,
  reducers: {
    saveProjectSummary: (state, action) => {
      state.projectSummary = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    saveWorkAreaOptions: (state, action) => {
      state.workAreaOptions = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    saveTradeOptions: (state, action) => {
      state.tradeOptions = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    saveWings: (state, action) => {
      state.wings = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    saveActivitiesByTrade: (state, action) => {
      const { tradeId, data } = action.payload;
      state.activitiesByTrade[tradeId] = data;
      state.isLoading = false;
      state.error = null;
    },
    saveSubActivitiesByActivity: (state, action) => {
      const { activityId, data } = action.payload;
      state.subActivitiesByActivity[activityId] = data;
      state.isLoading = false;
      state.error = null;
    },
    setMasterQuantityLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setMasterQuantityError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    saveMasterQuantitySubmitResponse: (state, action) => {
      state.submitResponse = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    resetMasterQuantityWorkState: (state) => {
      state.projectSummary = null;
      state.workAreaOptions = [];
      state.tradeOptions = [];
      state.wings = [];
      state.activitiesByTrade = {};
      state.subActivitiesByActivity = {};
      state.isLoading = false;
      state.error = null;
      state.submitResponse = null;
    },
  },
});

export const {
  saveProjectSummary,
  saveWorkAreaOptions,
  saveTradeOptions,
  saveWings,
  saveActivitiesByTrade,
  saveSubActivitiesByActivity,
  setMasterQuantityLoading,
  setMasterQuantityError,
  resetMasterQuantityWorkState,
  saveMasterQuantitySubmitResponse,
} = MasterQuantityWorkSlice.actions;

const mapOption = (item, labelKeyList = [], valueKeyList = []) => {
  const label =
    labelKeyList.map((key) => item?.[key]).find(Boolean) ||
    item?.label ||
    item?.name ||
    "";

  const value =
    valueKeyList
      .map((key) => item?.[key])
      .find((val) => val !== undefined && val !== null) ||
    item?.value ||
    item?.id ||
    "";

  return {
    label,
    value,
    raw: item,
  };
};

const getList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.response)) {
    return payload.response;
  }

  if (Array.isArray(payload?.result)) {
    return payload.result;
  }

  return [];
};

const getErrorMessage = (error, fallbackMessage = "Something went wrong") => {
  return error?.response?.data?.message || error?.message || fallbackMessage;
};

const startMasterQuantityLoader = (dispatch) => {
  dispatch(setLoader(true));
  dispatch(setMasterQuantityLoading(true));
};

const stopMasterQuantityLoader = (dispatch) => {
  dispatch(setLoader(false));
  dispatch(setMasterQuantityLoading(false));
};

const handleMasterQuantityError = ({
  dispatch,
  error,
  fallbackMessage,
  showToast = true,
}) => {
  const errorMessage = getErrorMessage(error, fallbackMessage);

  stopMasterQuantityLoader(dispatch);
  dispatch(setMasterQuantityError(errorMessage));

  if (showToast) {
    Toast({ type: "error", message: errorMessage });
  }

  return errorMessage;
};

const mapProjectSummary = (data = {}) => ({
  project_id: data?.project_id || "",
  project_name: data?.project_name || "",
  raw: data,
  wings_count: data?.wings_count || "",
  floor_count: data?.floors_count || "",
  flats_count: data?.flats_count || "",
});

const mapWorkAreaOption = (item) =>
  mapOption(
    item,
    ["area_name", "name", "label"],
    ["work_area_id", "id", "value"],
  );

const mapTradeOption = (item) =>
  mapOption(
    item,
    ["trade_title", "name", "label"],
    ["trade_id", "id", "value"],
  );

const mapActivityOption = (item) =>
  mapOption(
    item,
    ["activity_name", "name", "label"],
    ["activity_id", "id", "value"],
  );

const mapSubActivityOption = (item) =>
  mapOption(
    item,
    ["sub_activity_name", "name", "label"],
    ["sub_activity_id", "id", "value"],
  );

const mapOptionList = (payload, mapper) => {
  const list = getList(payload);
  const mappedList = [];

  for (const item of list) {
    mappedList.push(mapper(item));
  }

  return mappedList;
};

const createWorkAreaApiConfig = (query = {}) => {
  const baseConfig = MasterQuantityAddAPIs.getProjectWorkAreaOptionsApi;

  return {
    method: baseConfig.method,
    baseURL: baseConfig.baseURL,
    api: baseConfig.api(query.project_id, query.worklocationtype),
  };
};

const createActivityApiConfig = (query = {}) => {
  const baseConfig = MasterQuantityAddAPIs.getActivityOptionsApi;

  return {
    method: baseConfig.method,
    baseURL: baseConfig.baseURL,
    api: baseConfig.api(query.trade_id),
  };
};

const createSubActivityApiConfig = (query = {}) => {
  const baseConfig = MasterQuantityAddAPIs.getSubActivityOptionsApi;

  return {
    method: baseConfig.method,
    baseURL: baseConfig.baseURL,
    api: baseConfig.api(query.activity_id),
  };
};

const getProjectDataList = (res) => {
  const data = res?.data || res;
  return getList(data?.project_data || data);
};

const createWing = ({ wingId, wingName }) => ({
  label: wingName,
  value: wingId,
  floors: [],
  raw: {
    wings_id: wingId,
    wings_name: wingName,
  },
});

const createFloor = ({ floorId, floorName }) => ({
  label: floorName,
  value: floorId,
  units: [],
  raw: {
    floor_id: floorId,
    floor_name: floorName,
  },
});

const createUnit = ({ row, flatId, flatName }) => ({
  label: flatName,
  value: flatId,
  areas_of_work: row?.areas_of_work || [],
  external_or_internal: row?.external_or_internal ?? null,
  raw: row,
});

const findFloorById = (floorId, floors = []) => {
  return floors.find((floor) => String(floor.value) === String(floorId));
};

const hasUnit = (flatId, units = []) => {
  return units.some((unit) => String(unit.value) === String(flatId));
};

const getWingRowValues = (row = {}) => ({
  wingId: row?.wings_id,
  wingName: row?.wings_name || "",
  floorId: row?.floor_id,
  floorName: row?.floor_name || "",
  flatId: row?.flat_id,
  flatName: row?.flat_name || "N/A",
});

const ensureWing = (wingMap, rowValues) => {
  const { wingId, wingName } = rowValues;

  if (!wingMap[wingId]) {
    wingMap[wingId] = createWing({ wingId, wingName });
  }

  return wingMap[wingId];
};

const ensureFloor = (wingObj, rowValues) => {
  const { floorId, floorName } = rowValues;
  const existingFloor = findFloorById(floorId, wingObj.floors);

  if (existingFloor) {
    return existingFloor;
  }

  const newFloor = createFloor({ floorId, floorName });
  wingObj.floors.push(newFloor);
  return newFloor;
};

const addUnitIfMissing = (floorObj, row, rowValues) => {
  const { flatId, flatName } = rowValues;

  if (hasUnit(flatId, floorObj.units)) {
    return;
  }

  floorObj.units.push(createUnit({ row, flatId, flatName }));
};

const addWingTreeRow = (wingMap, row) => {
  const rowValues = getWingRowValues(row);
  const wingObj = ensureWing(wingMap, rowValues);
  const floorObj = ensureFloor(wingObj, rowValues);

  addUnitIfMissing(floorObj, row, rowValues);
};

const buildWingsTree = (responseList = []) => {
  const wingMap = {};

  for (const row of responseList) {
    addWingTreeRow(wingMap, row);
  }

  return Object.values(wingMap);
};

const buildSubmitSuccessMessage = (responseData) => {
  return responseData?.message || "Master quantity submitted successfully";
};

const buildUpdateSuccessMessage = (responseData) => {
  return responseData?.message || "Master quantity updated successfully";
};

export const getProjectSummaryAct = (projectId, query) => async (dispatch) => {
  MasterQuantityAddAPIs.getProjectSummaryApi.project_id = projectId;
  MasterQuantityAddAPIs.getProjectSummaryApi.work_location = 1;
  addQuery(query || {}, MasterQuantityAddAPIs.getProjectSummaryApi);

  startMasterQuantityLoader(dispatch);

  try {
    const res = await api({ ...MasterQuantityAddAPIs.getProjectSummaryApi });
    const data = res?.data || res;
    const mapped = mapProjectSummary(data);

    dispatch(saveProjectSummary(mapped));
    dispatch(setLoader(false));

    return mapped;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to fetch project summary",
    });

    throw errorMessage;
  }
};

export const getWorkAreaOptionsAct = (query) => async (dispatch) => {
  addQuery(query || {}, MasterQuantityAddAPIs.getProjectWorkAreaOptionsApi);

  startMasterQuantityLoader(dispatch);

  try {
    const apiConfig = createWorkAreaApiConfig(query);
    const res = await api(apiConfig);
    const list = mapOptionList(res?.data || res, mapWorkAreaOption);

    dispatch(saveWorkAreaOptions(list));
    dispatch(setLoader(false));

    return list;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to fetch work area options",
    });

    throw errorMessage;
  }
};

export const getTradeOptionsAct = (query) => async (dispatch) => {
  addQuery(query || {}, MasterQuantityAddAPIs.getTradeOptionsApi);

  startMasterQuantityLoader(dispatch);

  try {
    const res = await api({ ...MasterQuantityAddAPIs.getTradeOptionsApi });
    const list = mapOptionList(res?.data || res, mapTradeOption);

    dispatch(saveTradeOptions(list));
    dispatch(setLoader(false));

    return list;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to fetch trade options",
    });

    throw errorMessage;
  }
};

const getNumberFromText = (text = "") => {
  const match = String(text).match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const isCommonArea = (unit) => {
  return String(unit?.label || "").toLowerCase() === "common area";
};

const getUnitSortParts = (label = "") => {
  const value = String(label).trim();

  // Supports A-1373, N1149, B-1052
  const match = value.match(/^([a-zA-Z]+)-?(\d+)?/);

  return {
    prefix: match?.[1] || value,
    number: match?.[2] ? Number(match[2]) : 0,
  };
};

const sortUnits = (units = []) => {
  units.sort((a, b) => {
    // Common Area always last
    if (isCommonArea(a)) return 1;
    if (isCommonArea(b)) return -1;

    const unitA = getUnitSortParts(a?.label);
    const unitB = getUnitSortParts(b?.label);

    const prefixCompare = unitA.prefix.localeCompare(unitB.prefix);

    if (prefixCompare !== 0) {
      return prefixCompare;
    }

    return unitA.number - unitB.number;
  });

  return units;
};

const sortFloors = (floors = []) => {
  floors.sort((a, b) => {
    const floorA = getNumberFromText(a?.label);
    const floorB = getNumberFromText(b?.label);

    return floorA - floorB; // 3F, 4F, 5F, 6F...
  });

  return floors;
};

const getNumber = (value) => {
  const match = String(value).match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const sortWingsFloorsAndUnits = (wings = []) => {
  // Sort wings first
  wings.sort((a, b) => getNumber(a.label) - getNumber(b.label));

  wings.forEach((wing) => {
    if (!Array.isArray(wing?.floors)) return;

    // Existing floor sort
    sortFloors(wing.floors);

    wing.floors.forEach((floor) => {
      if (Array.isArray(floor?.units)) {
        // Existing unit sort
        sortUnits(floor.units);
      }
    });
  });

  return wings;
};

export const getWingsByProjectAct =
  (project_id, work_location) => async (dispatch) => {
    MasterQuantityAddAPIs.getProjectSummaryApi.project_id = project_id;
    MasterQuantityAddAPIs.getProjectSummaryApi.work_location = work_location;

    startMasterQuantityLoader(dispatch);
    dispatch(saveWings([]));

    try {
      const res = await api({ ...MasterQuantityAddAPIs.getProjectSummaryApi });
      const responseList = getProjectDataList(res);
      const list = buildWingsTree(responseList);
      debugger;
      sortWingsFloorsAndUnits(list);
      dispatch(saveWings(list));
      stopMasterQuantityLoader(dispatch);

      return list;
    } catch (error) {
      const errorMessage = handleMasterQuantityError({
        dispatch,
        error,
        fallbackMessage: "Failed to fetch wings",
      });

      throw errorMessage;
    }
  };

export const getActivityOptionsAct = (query) => async (dispatch) => {
  try {
    const apiConfig = createActivityApiConfig(query);
    const res = await api(apiConfig);
    const tradeId = query?.trade_id;
    const list = mapOptionList(res?.data || res, mapActivityOption);

    dispatch(saveActivitiesByTrade({ tradeId, data: list }));
    dispatch(setLoader(false));

    return list;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to fetch activity options",
    });

    throw errorMessage;
  }
};

export const getSubActivityOptionsAct = (query) => async (dispatch) => {
  try {
    const apiConfig = createSubActivityApiConfig(query);
    const res = await api(apiConfig);
    const activityId = query?.activity_id;
    const list = mapOptionList(res?.data || res, mapSubActivityOption);

    dispatch(saveSubActivitiesByActivity({ activityId, data: list }));
    dispatch(setLoader(false));

    return list;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to fetch sub activity options",
    });

    throw errorMessage;
  }
};

export const submitMasterQuantityAct = (body) => async (dispatch) => {
  startMasterQuantityLoader(dispatch);

  try {
    const res = await api({
      ...MasterQuantityAddAPIs.submitMasterQuantityApi,
      body,
    });

    const responseData = res?.data || res;

    dispatch(saveMasterQuantitySubmitResponse(responseData));
    stopMasterQuantityLoader(dispatch);

    Toast({
      type: "success",
      message: buildSubmitSuccessMessage(responseData),
    });

    return responseData;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to submit master quantity",
    });

    throw errorMessage;
  }
};

export const updateMasterQuantityAct = (body) => async (dispatch) => {
  startMasterQuantityLoader(dispatch);

  try {
    const res = await api({
      ...MasterQuantityAddAPIs.updateMasterQuantityApi,
      data: body,
    });

    const responseData = res?.data || res;

    dispatch(saveMasterQuantitySubmitResponse(responseData));
    dispatch(setLoader(false));

    Toast({
      type: "success",
      message: buildUpdateSuccessMessage(responseData),
    });

    return responseData;
  } catch (error) {
    const errorMessage = handleMasterQuantityError({
      dispatch,
      error,
      fallbackMessage: "Failed to update master quantity",
    });

    throw errorMessage;
  }
};

export const getMasterQuantityInitialAct =
  ({ project_id, worklocationtype }) =>
  async (dispatch) => {
    startMasterQuantityLoader(dispatch);

    try {
      await Promise.all([
        dispatch(getProjectSummaryAct(project_id, worklocationtype)),
        dispatch(getWorkAreaOptionsAct({ project_id, worklocationtype })),
        dispatch(getTradeOptionsAct()),
        dispatch(getWingsByProjectAct(project_id, worklocationtype)),
      ]);

      stopMasterQuantityLoader(dispatch);
    } catch (error) {
      console.log("Failed to Quantity the project", error);

      stopMasterQuantityLoader(dispatch);
    }
  };

export const getProjectInitialAct =
  ({ project_id, worklocationtype }) =>
  async (dispatch) => {
    startMasterQuantityLoader(dispatch);

    try {
      await dispatch(getProjectSummaryAct(project_id, worklocationtype));
      stopMasterQuantityLoader(dispatch);
    } catch (error) {
      console.log("Failed to Initiate the project", error);
      stopMasterQuantityLoader(dispatch);
    }
  };

export default MasterQuantityWorkSlice.reducer;
